import { Connection, PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import {
    createAssociatedTokenAccountInstruction,
    createTransferInstruction,
    getAccount,
    getAssociatedTokenAddress
} from '@solana/spl-token';

// Species themes for SolsticeStake
const speciesThemes = {
    balsam: {
        name: 'Balsam Fir',
        scientific: 'Abies balsamea',
        icon: '🎄',
        color: 'from-red-900/50 to-green-900/50',
        impact: 'Appalachian reforestation',
        location: 'Appalachian Mountains'
    },
    douglas: {
        name: 'Douglas Fir',
        scientific: 'Pseudotsuga menziesii',
        icon: '🌲',
        color: 'from-green-900/50 to-teal-900/50',
        impact: 'Pacific habitat restoration',
        location: 'Pacific Northwest'
    },
    fraser: {
        name: 'Fraser Fir',
        scientific: 'Abies fraseri',
        icon: '🌳',
        color: 'from-blue-900/50 to-cyan-900/50',
        impact: 'Endangered species conservation',
        location: 'Southern Appalachia'
    },
    noble: {
        name: 'Noble Fir',
        scientific: 'Abies procera',
        icon: '🎋',
        color: 'from-purple-900/50 to-indigo-900/50',
        impact: 'Cascade ecosystem health',
        location: 'Cascade Range'
    }
};

// Divine haikus for the star generator
const haikus = [
    {
        lines: [
            "Stellar light above",
            "Tree branches reach for the stars",
            "Wealth flows eternal"
        ]
    },
    {
        lines: [
            "Cosmic dance of light",
            "Sacred trees await their stars",
            "Fortune blooms within"
        ]
    },
    {
        lines: [
            "Radiant star burns",
            "Blessed tree yearns for its crown",
            "Destiny unfolds"
        ]
    },
    {
        lines: [
            "Celestial glow",
            "Nature's bounty intertwined",
            "Riches from above"
        ]
    },
    {
        lines: [
            "Stars guide the pathway",
            "Trees reach up to touch their light",
            "Abundance flows down"
        ]
    }
];

// Star patterns and color schemes
const starPatterns = [
    { name: "Radiant", weight: 0.28, flareBias: 1.0, haloBias: 0.7, sharpnessBias: 1.15, twinkleBias: 0.9, glowBias: 1.1 },
    { name: "Crowned", weight: 0.22, flareBias: 0.55, haloBias: 1.1, sharpnessBias: 1.3, twinkleBias: 0.7, glowBias: 0.9 },
    { name: "Nova", weight: 0.2, flareBias: 1.15, haloBias: 0.85, sharpnessBias: 1.4, twinkleBias: 1.0, glowBias: 1.2 },
    { name: "Halo", weight: 0.16, flareBias: 0.4, haloBias: 1.25, sharpnessBias: 0.95, twinkleBias: 0.8, glowBias: 1.15 },
    { name: "Pulse", weight: 0.14, flareBias: 0.8, haloBias: 0.75, sharpnessBias: 1.05, twinkleBias: 1.15, glowBias: 0.95 }
];

const colorSchemes = [
    { name: "Aurora Gold", colors: [0.15, 1.3, 3.6], weight: 0.26 },
    { name: "Blue-White", colors: [0.0, 2.1, 4.2], weight: 0.24 },
    { name: "Ember", colors: [0.45, 1.1, 2.4], weight: 0.2 },
    { name: "Violet Ion", colors: [3.2, 4.1, 5.2], weight: 0.16 },
    { name: "Prismatic", colors: [1.0, 3.0, 5.1], weight: 0.14 }
];

const unlockOptions = {
    "0": { label: "Instant Harvest", months: 0, multiplier: 1.0, energy: "⚡", description: "Present moment awareness - instant Epiphany harvest", celestial: "Epiphany" },
    "3": { label: "Seasonal Root", months: 3, multiplier: 1.3, energy: "🌱", description: "Earth's quarterly rhythm - Spring Equinox alignment", celestial: "Spring Equinox" },
    "6": { label: "Solar Balance", months: 6, multiplier: 1.6, energy: "☀️", description: "Solstice-to-solstice harmony - Summer Solstice peak", celestial: "Summer Solstice" },
    "9": { label: "Gestation Cycle", months: 9, multiplier: 1.9, energy: "🌙", description: "Human creation rhythm - Autumn Equinox completion", celestial: "Autumn Equinox" },
    "12": { label: "Cosmic Round", months: 12, multiplier: 2.4, energy: "🌌", description: "Full zodiac/year completion - maximum revelation", celestial: "Winter Solstice" }
};

const UNLOCK_STORAGE_KEY = "starBondUnlock";
const TREE_DRAFT_KEY = "treeBondDraft";
const BOND_DRAFT_KEY = "treeBondPayload";
const MINT_IDLE_LABEL = "Finalize Reality Bond";
const MINT_WORKING_LABEL = "Finalizing...";
const MINT_DONE_LABEL = "Bond Draft Ready";
const MINT_BUTTON_IDS = ['bondPayButton'];
const TREE_PREVIEW_STAGE_IDS = ['treePreviewStage', 'treePreviewStageDrawer'];
const DEFAULT_CSEAS_MINT = '92dLaNpWekXPkqiFXKmBgEGjfzZJ5bC79AKeLaqAjXUo';
const DEFAULT_TREASURY = 'TREASURY_WALLET_TBD';
const DEFAULT_SOLANA_RPC = 'https://mainnet.helius-rpc.com/?api-key=92d64653-4629-4356-9ac8-38a15973cc1b';
const CSEAS_DECIMALS = 9;
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
const USDC_DECIMALS = 6;
const PAYMENT_TOKEN_KEY = 'bondPaymentToken';

function getElements(ids = []) {
    return ids
        .map((id) => document.getElementById(id))
        .filter(Boolean);
}

function setTextContent(ids = [], value = '') {
    getElements(ids).forEach((el) => {
        el.textContent = value;
    });
}

function resolveWalletAddress() {
    const walletStore = window.Alpine?.store ? window.Alpine.store('wallet') : null;
    if (walletStore?.isConnected && walletStore.address) {
        localStorage.setItem('walletAddress', walletStore.address);
        return walletStore.address;
    }

    const stored = localStorage.getItem('walletAddress');
    return stored && stored !== 'null' ? stored : null;
}

window.addEventListener('wallet-connected', (event) => {
    const address = event?.detail?.address;
    if (address) {
        localStorage.setItem('walletAddress', address);
    }
    updateBondPayButton();
});

window.addEventListener('wallet-disconnected', () => {
    localStorage.removeItem('walletAddress');
    updateBondPayButton();
});

function parseTreasuryList(value) {
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    if (typeof value !== 'string') return [];
    return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

function pickTreasury(treasuries, seedValue) {
    if (!treasuries.length) return null;
    if (treasuries.length === 1) return treasuries[0];
    const seed = seedValue || '';
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
        hash = (hash * 31 + seed.charCodeAt(i)) % 2147483647;
    }
    return treasuries[Math.abs(hash) % treasuries.length];
}

function getPaymentConfig(draft) {
    const dataset = document.body?.dataset || {};
    const mint = draft?.cseasMint || dataset.cseasMint || DEFAULT_CSEAS_MINT;
    const treasuryRaw = draft?.cseasTreasury || dataset.cseasTreasury || DEFAULT_TREASURY;
    const treasuries = parseTreasuryList(treasuryRaw);
    const rpcUrl = draft?.solanaRpc || dataset.solanaRpc || DEFAULT_SOLANA_RPC;
    return { mint, treasuries, rpcUrl };
}

function getCseasAmount(draft) {
    if (!draft) return null;
    if (Number.isFinite(draft.costCseas) && draft.costCseas > 0) {
        return draft.costCseas;
    }
    if (Number.isFinite(draft.costUsd) && Number.isFinite(draft.cseasPriceUsd) && draft.cseasPriceUsd > 0) {
        return draft.costUsd / draft.cseasPriceUsd;
    }
    return null;
}

function toBaseUnits(amount, decimals) {
    const normalized = typeof amount === 'number' ? amount.toString() : String(amount);
    const [whole, fraction = ''] = normalized.split('.');
    const padded = `${fraction}${'0'.repeat(decimals)}`.slice(0, decimals);
    const digits = `${whole}${padded}`.replace(/^0+/, '') || '0';
    return BigInt(digits);
}

function getSelectedPaymentToken() {
    const selected = document.querySelector('input[name="bondPayment"]:checked');
    return selected?.value || 'sol';
}

function savePaymentToken(token) {
    localStorage.setItem(PAYMENT_TOKEN_KEY, token);
}

function loadPaymentToken() {
    const stored = localStorage.getItem(PAYMENT_TOKEN_KEY);
    return stored || 'sol';
}

function computePaymentAmounts(draft) {
    if (!draft) {
        return { sol: null, usdc: null };
    }
    const usd = Number(draft.costUsd || 0);
    if (!Number.isFinite(usd) || usd <= 0) {
        return { sol: null, usdc: null };
    }

    let solAmount = null;
    const priceUsd = Number(draft.cseasPriceUsd || 0);
    const priceSol = Number(draft.cseasPriceSol || 0);
    if (priceUsd > 0 && priceSol > 0) {
        const solUsd = priceUsd / priceSol;
        if (Number.isFinite(solUsd) && solUsd > 0) {
            solAmount = usd / solUsd;
        }
    }

    return {
        sol: solAmount,
        usdc: usd
    };
}

function updatePaymentUI() {
    const draft = loadTreeDraft();
    const amounts = computePaymentAmounts(draft);
    const solLabel = document.getElementById('bondPaySolAmount');
    const usdcLabel = document.getElementById('bondPayUsdcAmount');
    const hint = document.getElementById('bondPayHint');

    if (solLabel) {
        solLabel.textContent = amounts.sol ? `${amounts.sol.toFixed(4)} SOL` : 'Unavailable';
        solLabel.classList.toggle('opacity-40', !amounts.sol);
    }
    if (usdcLabel) {
        usdcLabel.textContent = amounts.usdc ? `${amounts.usdc.toFixed(2)} USDC` : 'Unavailable';
        usdcLabel.classList.toggle('opacity-40', !amounts.usdc);
    }

    if (hint) {
        hint.textContent = 'Select your payment method.';
    }

    updateBondPayButton();
}

function updateBondPayButton() {
    const button = document.getElementById('bondPayButton');
    if (!button) return;

    const walletStore = window.Alpine?.store ? window.Alpine.store('wallet') : null;
    const isConnected = Boolean(walletStore?.isConnected);
    if (walletStore?.isConnecting) {
        button.innerHTML = '<span class="text-xl mr-2">⏳</span>Connecting...';
        button.disabled = true;
        return;
    }
    const draft = loadTreeDraft();
    const amounts = computePaymentAmounts(draft);
    const token = getSelectedPaymentToken();
    const amount = token === 'usdc' ? amounts.usdc : amounts.sol;

    if (!isConnected) {
        button.innerHTML = '<span class="text-xl mr-2">💳</span>Connect Wallet to Pay';
        button.disabled = false;
        return;
    }

    if (!amount || !Number.isFinite(amount) || amount <= 0) {
        button.innerHTML = '<span class="text-xl mr-2">⚠️</span>Payment Unavailable';
        button.disabled = true;
        return;
    }

    const label = token === 'usdc'
        ? `Pay ${amount.toFixed(2)} USDC & Mint`
        : `Pay ${amount.toFixed(4)} SOL & Mint`;
    button.innerHTML = `<span class="text-xl mr-2">⚡</span>${label}`;
    button.disabled = false;
}

async function handleBondPayment() {
    const walletStore = window.Alpine?.store ? window.Alpine.store('wallet') : null;
    if (!walletStore?.isConnected) {
        if (typeof walletStore?.connect === 'function') {
            setTreeSummaryStatus('Connecting wallet...');
            updateBondPayButton();
            try {
                const connected = await walletStore.connect();
                updateBondPayButton();
                if (!connected) {
                    setTreeSummaryStatus(walletStore.connectionError || 'Wallet connection failed.', true);
                    return;
                }
            } catch (error) {
                updateBondPayButton();
                setTreeSummaryStatus(error.message || 'Wallet connection failed.', true);
                return;
            }
        } else {
            setTreeSummaryStatus('Wallet adapter unavailable. Refresh and try again.', true);
            return;
        }
    }

    if (!window.starGenerator) {
        setTreeSummaryStatus('Star generator not ready yet.', true);
        return;
    }

    window.starGenerator.mintStar();
}

async function sendPaymentTransfer({ amount, walletAddress, config, treeId, token }) {
    if (!window.solana?.isPhantom) {
        throw new Error('Wallet not available');
    }
    if (!config?.treasuries?.length) {
        throw new Error('Missing treasury configuration');
    }
    if (!amount || amount <= 0) {
        throw new Error('Payment amount must be greater than 0');
    }

    const treasuryAddress = pickTreasury(config.treasuries, treeId || walletAddress);
    if (!treasuryAddress || treasuryAddress === DEFAULT_TREASURY) {
        throw new Error('Treasury wallets are not configured yet');
    }

    const connection = new Connection(config.rpcUrl, 'confirmed');
    const owner = new PublicKey(walletAddress);
    const treasury = new PublicKey(treasuryAddress);
    const tx = new Transaction();

    if (token === 'usdc') {
        const mint = new PublicKey(USDC_MINT);
        const fromAta = await getAssociatedTokenAddress(mint, owner);
        const toAta = await getAssociatedTokenAddress(mint, treasury);

        try {
            await getAccount(connection, fromAta);
        } catch (error) {
            throw new Error('No USDC token account found. Please acquire USDC first.');
        }

        try {
            await getAccount(connection, toAta);
        } catch (error) {
            tx.add(createAssociatedTokenAccountInstruction(owner, toAta, treasury, mint));
        }

        const baseUnits = toBaseUnits(amount, USDC_DECIMALS);
        if (baseUnits <= 0n) {
            throw new Error('Invalid USDC amount');
        }

        tx.add(createTransferInstruction(fromAta, toAta, owner, baseUnits));
    } else {
        const baseUnits = toBaseUnits(amount, 9);
        if (baseUnits <= 0n) {
            throw new Error('Invalid SOL amount');
        }
        const lamports = Number(baseUnits);
        if (!Number.isFinite(lamports) || lamports <= 0) {
            throw new Error('Invalid SOL amount');
        }
        tx.add(SystemProgram.transfer({
            fromPubkey: owner,
            toPubkey: treasury,
            lamports
        }));
    }

    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
    tx.recentBlockhash = blockhash;
    tx.feePayer = owner;

    const signed = await window.solana.signAndSendTransaction(tx);
    const signature = signed?.signature || signed;

    const confirmation = await connection.confirmTransaction(
        { signature, blockhash, lastValidBlockHeight },
        'finalized'
    );
    if (confirmation?.value?.err) {
        console.error('Transaction confirmation error:', confirmation.value.err);
        const errJson = JSON.stringify(confirmation.value.err);
        throw new Error(`Transaction failed to confirm: ${errJson}`);
    }

    const status = await connection.getSignatureStatuses([signature], { searchTransactionHistory: true });
    const confirmationStatus = status?.value?.[0]?.confirmationStatus;
    if (confirmationStatus && confirmationStatus !== 'finalized') {
        throw new Error('Transaction not finalized yet');
    }

    return { signature, treasuryAddress };
}

async function verifyBondPayment({ bondRecordId, bondId, txSignature, rpcUrl }) {
    const response = await fetch('/api/verify-bond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            bondRecordId,
            bondId,
            txSignature,
            rpcUrl
        })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const message = data.error || data.details || 'Verification failed';
        throw new Error(message);
    }
    return data;
}

async function waitForBondVerification({ bondRecordId, bondId, txSignature, rpcUrl }) {
    const maxAttempts = 12;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        setTreeSummaryStatus(`Verifying payment on-chain (${attempt}/${maxAttempts})...`);
        try {
            const data = await verifyBondPayment({ bondRecordId, bondId, txSignature, rpcUrl });
            if (data?.verified || data?.verificationStatus === 'verified') {
                return data;
            }
        } catch (error) {
            if (attempt === maxAttempts) {
                throw error;
            }
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
    }
    return null;
}

// Shader sources
const vertexShaderSource = `
    attribute vec4 aPosition;
    void main() {
        gl_Position = aPosition;
    }
`;

const fragmentShaderSource = `
    precision mediump float;
    uniform float time;
    uniform vec2 resolution;
    uniform float uNumPoints;
    uniform float uInnerRadius;
    uniform float uOuterRadius;
    uniform float uSharpness;
    uniform float uGlow;
    uniform float uHalo;
    uniform float uFlare;
    uniform float uFlareAngle;
    uniform float uTwinkle;
    uniform float uTwinkleSpeed;
    uniform float uPulseRate;
    uniform float uIntensity;
    uniform float uRotationSpeed;
    uniform float uColorShift;
    uniform float uColorPhase;

    vec2 rotate(vec2 p, float a) {
        float c = cos(a);
        float s = sin(a);
        return vec2(c * p.x - s * p.y, s * p.x + c * p.y);
    }

    float starDistance(vec2 p, float points, float innerR, float outerR, float sharpness) {
        float angle = atan(p.y, p.x);
        float r = length(p);
        float spokes = 0.5 + 0.5 * cos(angle * points);
        spokes = pow(spokes, sharpness);
        float radius = mix(innerR, outerR, spokes);
        return radius - r;
    }

    vec3 palette(float t, float shift) {
        vec3 phase = vec3(0.0, 0.33, 0.67);
        return 0.5 + 0.5 * cos(6.28318 * (t + phase + shift));
    }

    float flareLayer(vec2 p, float falloff, float width) {
        float x = exp(-abs(p.x) * falloff) * exp(-abs(p.y) * width);
        float y = exp(-abs(p.y) * falloff) * exp(-abs(p.x) * width);
        return x + y;
    }

    void main() {
        vec2 uv = (gl_FragCoord.xy - 0.5 * resolution) / min(resolution.x, resolution.y);
        float px = 1.5 / min(resolution.x, resolution.y);
        float t = time;
        vec2 p = rotate(uv, t * uRotationSpeed);

        float starDist = starDistance(p, uNumPoints, uInnerRadius, uOuterRadius, uSharpness);
        float starMask = smoothstep(-px, px, starDist);

        float baseTwinkle = 0.8 + 0.2 * sin(t * uTwinkleSpeed + uColorPhase * 4.0);
        float twinkle = mix(1.0, baseTwinkle, uTwinkle);
        float pulse = 0.8 + 0.2 * sin(t * uPulseRate);
        float radial = clamp(1.0 - length(p) / uOuterRadius, 0.0, 1.0);

        vec3 starColor = palette(radial + uColorPhase, uColorShift);
        vec3 coreColor = vec3(1.0, 0.97, 0.9);

        vec3 color = vec3(0.01, 0.015, 0.03) + vec3(0.02, 0.01, 0.03) * (uv.y + 0.5);
        color += mix(starColor, coreColor, pow(radial, 2.5)) * starMask * uIntensity * pulse;

        float glow = uGlow * exp(-length(p) * 6.0) * (0.6 + 0.4 * twinkle);
        color += starColor * glow;

        float halo = uHalo * smoothstep(uOuterRadius + 0.35, uOuterRadius, length(p));
        color += starColor * halo * 0.6;

        vec2 flareP = rotate(uv, uFlareAngle);
        float flare = flareLayer(flareP, 12.0, 3.0) + flareLayer(rotate(flareP, 0.785398), 9.0, 2.5);
        color += coreColor * flare * uFlare * twinkle;

        gl_FragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
    }
`;

// Helper functions
function generateSeed() {
    return Math.floor(Math.random() * 1000000);
}

function createRandom(seed) {
    return function() {
        seed = (seed * 16807) % 2147483647;
        return (seed - 1) / 2147483646;
    };
}

function weightedRandom(items, random) {
    const weights = items.map(item => item.weight);
    let total = weights.reduce((a, b) => a + b);
    let r = random() * total;
    
    for (let i = 0; i < items.length; i++) {
        if (r < weights[i]) return items[i];
        r -= weights[i];
    }
    return items[0];
}

function createShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compilation error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

function addMonths(date, months) {
    const result = new Date(date.getTime());
    const targetDay = result.getDate();
    result.setMonth(result.getMonth() + months);
    if (result.getDate() < targetDay) {
        result.setDate(0);
    }
    return result;
}

function getNextChristmasDate(baseDate) {
    const year = baseDate.getFullYear();
    const christmas = new Date(year, 11, 25, 0, 0, 0, 0);
    if (baseDate <= christmas) {
        return christmas;
    }
    return new Date(year + 1, 11, 25, 0, 0, 0, 0);
}

function formatUnlockDate(date) {
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    }).format(date);
}

function getSelectedUnlockValue() {
    const selected = document.querySelector('input[name="bondUnlock"]:checked');
    return selected?.value || "christmas";
}

function computeUnlockDate(selection, baseDate) {
    const option = unlockOptions[selection];
    if (!option) {
        return getNextChristmasDate(baseDate);
    }
    if (option.months) {
        return addMonths(baseDate, option.months);
    }
    return getNextChristmasDate(baseDate);
}

function loadUnlockSelection() {
    try {
        const raw = localStorage.getItem(UNLOCK_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.warn('Failed to load unlock selection:', error);
        return null;
    }
}

function saveUnlockSelection(selection) {
    localStorage.setItem(UNLOCK_STORAGE_KEY, JSON.stringify({ selection }));
}

function updateUnlockUI() {
    const now = new Date();
    const selection = getSelectedUnlockValue();
    const preview = document.getElementById('unlockPreview');
    const selectedDate = computeUnlockDate(selection, now);

    document.querySelectorAll('[data-unlock-date]').forEach((el) => {
        const value = el.getAttribute('data-unlock-date');
        const date = computeUnlockDate(value, now);
        el.textContent = `Unlocks ${formatUnlockDate(date)}`;
    });

    if (preview) {
        preview.textContent = `Unlocks on ${formatUnlockDate(selectedDate)}`;
    }
    updateBondSummaryUI();
    updateImpactDisplay();
}

function initUnlockSelection() {
    const radios = Array.from(document.querySelectorAll('input[name="bondUnlock"]'));
    if (!radios.length) return;

    const stored = loadUnlockSelection();
    if (stored?.selection) {
        const match = radios.find((radio) => radio.value === stored.selection);
        if (match) {
            match.checked = true;
        }
    }

    radios.forEach((radio) => {
        radio.addEventListener('change', () => {
            saveUnlockSelection(radio.value);
            updateUnlockUI();
        });
    });

    updateUnlockUI();
}

function buildUnlockPayload() {
    const selection = getSelectedUnlockValue();
    const now = new Date();
    const unlockDate = computeUnlockDate(selection, now);
    const option = unlockOptions[selection];

    return {
        selection,
        unlockAt: unlockDate.toISOString(),
        lockMonths: option?.months || null,
        multiplier: option?.multiplier || 1.0,
        energy: option?.energy || "⚡",
        label: option?.label || "Unknown",
        createdAt: now.toISOString()
    };
}

function loadTreeDraft() {
    try {
        const raw = localStorage.getItem(TREE_DRAFT_KEY);
        const draft = raw ? JSON.parse(raw) : null;
        const urlTreeId = new URLSearchParams(window.location.search).get('treeId');
        if (draft && urlTreeId && !draft.treeId) {
            draft.treeId = urlTreeId;
            localStorage.setItem(TREE_DRAFT_KEY, JSON.stringify(draft));
        }
        return draft;
    } catch (error) {
        console.warn('Failed to load tree draft:', error);
        return null;
    }
}

function formatUsd(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) return '$0.000';
    return `$${value.toFixed(3)}`;
}

function formatCseas(costUsd, priceUsd) {
    if (!priceUsd || priceUsd <= 0) return '--';
    const amount = costUsd / priceUsd;
    if (!Number.isFinite(amount)) return '--';
    return `${amount.toFixed(3)} CSEAS`;
}

function setTreeSummaryStatus(message, isError = false) {
    const statusEls = getElements(['treeSummaryStatus', 'treeSummaryStatusCompact']);
    if (!statusEls.length) return;
    statusEls.forEach((statusEl) => {
        statusEl.textContent = message || '';
        statusEl.classList.toggle('error', Boolean(isError));
    });
}

function updateTreeSummaryUI() {
    const draft = loadTreeDraft();
    const mintButtons = getElements(MINT_BUTTON_IDS);

    if (!draft) {
        setTextContent(['treeSummaryName', 'treeSummaryNameCompact'], 'No tree found');
        setTextContent(['treeSummaryOrnaments', 'treeSummaryOrnamentsCompact'], '--');
        setTextContent(['treeSummaryUsd', 'treeSummaryUsdCompact'], '--');
        setTextContent(['treeSummaryCseas', 'treeSummaryCseasCompact'], '--');
        mintButtons.forEach((btn) => { btn.disabled = true; });
        setTreeSummaryStatus('Decorate your tree first, then return here to finalize the bond.', true);
        renderTreePreview(null);
        updateBondSummaryUI();
        updatePaymentUI();
        return;
    }

    const ornamentCount = draft.ornamentCount ?? draft.decorations?.length ?? 0;
    setTextContent(['treeSummaryName', 'treeSummaryNameCompact'], draft.name || 'Untitled');
    setTextContent(['treeSummaryOrnaments', 'treeSummaryOrnamentsCompact'], `${ornamentCount}`);
    setTextContent(['treeSummaryUsd', 'treeSummaryUsdCompact'], formatUsd(draft.costUsd || 0));
    setTextContent(['treeSummaryCseas', 'treeSummaryCseasCompact'], formatCseas(draft.costUsd || 0, draft.cseasPriceUsd));
    mintButtons.forEach((btn) => { btn.disabled = false; });
    setTreeSummaryStatus('Tree draft loaded. Lock in your star to complete the bond.');
    renderTreePreview(draft);
    updateBondSummaryUI();
    updateImpactDisplay();
    updatePaymentUI();
}

function renderTreePreview(draft) {
    const stages = getElements(TREE_PREVIEW_STAGE_IDS);
    if (!stages.length) return;

    stages.forEach((stage) => {
        stage.innerHTML = '';
        const star = document.createElement('div');
        star.className = 'tree-preview-star';
        stage.appendChild(star);

        if (!draft?.decorations?.length) {
            stage.classList.add('tree-preview-empty');
            return;
        }

        stage.classList.remove('tree-preview-empty');
        const width = stage.clientWidth || 240;
        const height = stage.clientHeight || 360;
        const scaleX = width / 400;
        const scaleY = height / 600;

        draft.decorations.forEach((decoration) => {
            if (!decoration) return;
            const ornament = document.createElement('span');
            ornament.className = 'tree-preview-ornament';
            ornament.textContent = decoration.ornament || '*';
            ornament.style.left = `${decoration.x * scaleX}px`;
            ornament.style.top = `${decoration.y * scaleY}px`;
            stage.appendChild(ornament);
        });
    });
}

function updateBondSummaryUI() {
    const draft = loadTreeDraft();

    if (!draft) {
        setTextContent(['bondSummaryTotal', 'bondSummaryTotalCompact'], '--');
        setTextContent(['bondSummaryUsd', 'bondSummaryUsdCompact'], '--');
        setTextContent(['bondSummaryMaturity', 'bondSummaryMaturityCompact'], '--');
        setTextContent(['bondSummaryStar', 'bondSummaryStarCompact'], '--');
        setTextContent(['bondSummaryStatus', 'bondSummaryStatusCompact'], 'Complete your tree decoration to reveal staking totals.');
        return;
    }

    const totalUsd = draft.costUsd || 0;
    const totalCseas = formatCseas(totalUsd, draft.cseasPriceUsd);
    const maturityDate = computeUnlockDate(getSelectedUnlockValue(), new Date());
    const starSeed = window.starGenerator?.currentSeed;

    setTextContent(['bondSummaryTotal', 'bondSummaryTotalCompact'], totalCseas);
    setTextContent(['bondSummaryUsd', 'bondSummaryUsdCompact'], formatUsd(totalUsd));
    setTextContent(['bondSummaryMaturity', 'bondSummaryMaturityCompact'], formatUnlockDate(maturityDate));
    setTextContent(['bondSummaryStar', 'bondSummaryStarCompact'], starSeed ? `#${String(starSeed).padStart(6, '0')}` : '--');
    setTextContent(['bondSummaryStatus', 'bondSummaryStatusCompact'], 'Maturity set by your unlock selection. Finalize to stake and pay.');
}

class StarGenerator {
    constructor(canvas) {
        this.currentHaikuIndex = Math.floor(Math.random() * haikus.length);
        this.canvas = canvas;
        this.gl = canvas.getContext('webgl');
        this.currentSeed = generateSeed();
        this.random = createRandom(this.currentSeed);
        this.params = {};
        
        if (!this.gl) {
            console.error('WebGL not supported');
            throw new Error('WebGL not supported');
        }
        
        this.initWebGL();
        this.initializeParameters();
        this.updateStarId();
        this.updateParamsDisplay();
        requestAnimationFrame(this.render.bind(this));
    }
    
    initWebGL() {
        const gl = this.gl;
        
        const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
        
        if (!vertexShader || !fragmentShader) {
            throw new Error('Shader creation failed');
        }
        
        this.program = gl.createProgram();
        gl.attachShader(this.program, vertexShader);
        gl.attachShader(this.program, fragmentShader);
        gl.linkProgram(this.program);
        
        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            console.error('Program linking error:', gl.getProgramInfoLog(this.program));
            throw new Error('Program linking error');
        }
        
        const positions = new Float32Array([
            -1, -1,
            1, -1,
            -1, 1,
            1, 1
        ]);
        
        const positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
        
        this.locations = {
            position: gl.getAttribLocation(this.program, 'aPosition'),
            time: gl.getUniformLocation(this.program, 'time'),
            resolution: gl.getUniformLocation(this.program, 'resolution'),
            numPoints: gl.getUniformLocation(this.program, 'uNumPoints'),
            innerRadius: gl.getUniformLocation(this.program, 'uInnerRadius'),
            outerRadius: gl.getUniformLocation(this.program, 'uOuterRadius'),
            sharpness: gl.getUniformLocation(this.program, 'uSharpness'),
            glow: gl.getUniformLocation(this.program, 'uGlow'),
            halo: gl.getUniformLocation(this.program, 'uHalo'),
            flare: gl.getUniformLocation(this.program, 'uFlare'),
            flareAngle: gl.getUniformLocation(this.program, 'uFlareAngle'),
            twinkle: gl.getUniformLocation(this.program, 'uTwinkle'),
            twinkleSpeed: gl.getUniformLocation(this.program, 'uTwinkleSpeed'),
            pulseRate: gl.getUniformLocation(this.program, 'uPulseRate'),
            intensity: gl.getUniformLocation(this.program, 'uIntensity'),
            rotationSpeed: gl.getUniformLocation(this.program, 'uRotationSpeed'),
            colorShift: gl.getUniformLocation(this.program, 'uColorShift'),
            colorPhase: gl.getUniformLocation(this.program, 'uColorPhase')
        };
        
        gl.useProgram(this.program);
        gl.enableVertexAttribArray(this.locations.position);
        gl.vertexAttribPointer(this.locations.position, 2, gl.FLOAT, false, 0, 0);
    }
    
    initializeParameters() {
        const pattern = weightedRandom(starPatterns, this.random);
        const scheme = weightedRandom(colorSchemes, this.random);
        
        const innerRadius = 0.18 + this.random() * 0.12;
        const outerBase = 0.48 + this.random() * 0.2;
        const outerRadius = Math.max(outerBase, innerRadius + 0.18);
        const sharpness = Math.min(4.8, (2.0 + this.random() * 2.8) * (pattern.sharpnessBias || 1));
        const glow = Math.min(1.25, (0.45 + this.random() * 0.65) * (pattern.glowBias || 1));
        const halo = Math.min(1.25, (0.4 + this.random() * 0.7) * (pattern.haloBias || 1));
        const flare = Math.min(1.4, (0.3 + this.random() * 0.9) * (pattern.flareBias || 1));
        const twinkle = Math.min(1.0, (0.2 + this.random() * 0.8) * (pattern.twinkleBias || 1));

        this.params = {
            pattern: pattern.name,
            colorScheme: scheme.name,
            numPoints: Math.floor(this.random() * 4) + 5,
            innerRadius,
            outerRadius,
            sharpness,
            glow,
            halo,
            flare,
            flareAngle: this.random() * 0.785398,
            twinkle,
            twinkleSpeed: 0.4 + this.random() * 1.4,
            pulseRate: 0.6 + this.random() * 1.6,
            intensity: 0.75 + this.random() * 0.65,
            rotationSpeed: (this.random() * 0.16) - 0.08,
            colorShift: scheme.colors[0],
            colorPhase: scheme.colors[1] + this.random() * 0.7
        };
    }
    
    getRarityScore() {
        let score = 0;
        score += this.params.numPoints >= 7 ? 18 : this.params.numPoints >= 6 ? 12 : 0;
        score += this.params.sharpness > 3.6 ? 18 : this.params.sharpness > 3.0 ? 10 : 0;
        score += this.params.flare > 1.0 ? 18 : this.params.flare > 0.75 ? 10 : 0;
        score += this.params.halo > 1.0 ? 14 : this.params.halo > 0.7 ? 8 : 0;
        score += this.params.twinkle > 0.8 ? 10 : 0;
        score += this.params.outerRadius > 0.62 ? 10 : 0;
        score += this.params.intensity > 1.1 ? 12 : 0;
        return Math.min(score, 100);
    }
    
    getRarityTier(score) {
        if (score >= 80) return "Legendary ★★★★★";
        if (score >= 60) return "Epic ★★★★";
        if (score >= 40) return "Rare ★★★";
        if (score >= 20) return "Uncommon ★★";
        return "Common ★";
    }
    
    updateStarId() {
        const starIdEl = document.getElementById('starId');
        if (starIdEl) {
            starIdEl.textContent = `Star #${String(this.currentSeed).padStart(6, '0')}`;
        }
        updateBondSummaryUI();
    }
    
    updateParamsDisplay() {
        const score = this.getRarityScore();
        const tier = this.getRarityTier(score);
        const paramsDiv = document.getElementById('params') || document.getElementById('params-drawer');
        if (!paramsDiv) return;
        paramsDiv.innerHTML = `
            <div class="param-row">Pattern: ${this.params.pattern}</div>
            <div class="param-row">Color Scheme: ${this.params.colorScheme}</div>
            <div class="param-row">Points: ${this.params.numPoints}</div>
            <div class="param-row">Sharpness: ${this.params.sharpness.toFixed(2)}</div>
            <div class="param-row">Glow: ${Math.round(this.params.glow * 100)}%</div>
            <div class="param-row">Halo: ${Math.round(this.params.halo * 100)}%</div>
            <div class="param-row">Flare: ${Math.round(this.params.flare * 100)}%</div>
            <div class="param-row">Twinkle: ${Math.round(this.params.twinkle * 100)}%</div>
            <div class="rarity">Rarity: ${tier}</div>
        `;
    }
    
    resizeCanvasToDisplaySize() {
        const pixelRatio = window.devicePixelRatio || 1;
        const width = Math.floor(this.canvas.clientWidth * pixelRatio);
        const height = Math.floor(this.canvas.clientHeight * pixelRatio);
        
        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas.width = width;
            this.canvas.height = height;
            this.gl.viewport(0, 0, width, height);
        }
    }
    
    render(time) {
        this.resizeCanvasToDisplaySize();
        const gl = this.gl;
        
        gl.uniform1f(this.locations.time, time * 0.001);
        gl.uniform2f(this.locations.resolution, this.canvas.width, this.canvas.height);
        gl.uniform1f(this.locations.numPoints, this.params.numPoints);
        gl.uniform1f(this.locations.innerRadius, this.params.innerRadius);
        gl.uniform1f(this.locations.outerRadius, this.params.outerRadius);
        gl.uniform1f(this.locations.sharpness, this.params.sharpness);
        gl.uniform1f(this.locations.glow, this.params.glow);
        gl.uniform1f(this.locations.halo, this.params.halo);
        gl.uniform1f(this.locations.flare, this.params.flare);
        gl.uniform1f(this.locations.flareAngle, this.params.flareAngle);
        gl.uniform1f(this.locations.twinkle, this.params.twinkle);
        gl.uniform1f(this.locations.twinkleSpeed, this.params.twinkleSpeed);
        gl.uniform1f(this.locations.pulseRate, this.params.pulseRate);
        gl.uniform1f(this.locations.intensity, this.params.intensity);
        gl.uniform1f(this.locations.rotationSpeed, this.params.rotationSpeed);
        gl.uniform1f(this.locations.colorShift, this.params.colorShift);
        gl.uniform1f(this.locations.colorPhase, this.params.colorPhase);
        
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        
        requestAnimationFrame(this.render.bind(this));
    }
    
    generateNewStar() {
        this.currentSeed = generateSeed();
        this.random = createRandom(this.currentSeed);
        this.initializeParameters();
        this.updateStarId();
        this.updateParamsDisplay();
        this.rotateHaiku();
        updateRarityBadge();
    }
    
    async mintStar() {
        const mintButtons = getElements(MINT_BUTTON_IDS);
        const treeDraft = loadTreeDraft();

        if (!treeDraft) {
            setTreeSummaryStatus('Decorate your tree first before finalizing to bond.', true);
            return;
        }

        const treeId = treeDraft.treeId || new URLSearchParams(window.location.search).get('treeId');
        if (!treeId) {
            setTreeSummaryStatus('Tree not found. Please decorate and save your tree first.', true);
            return;
        }

        const walletAddress = resolveWalletAddress();
        if (!walletAddress) {
            setTreeSummaryStatus('Connect your wallet before finalizing the bond.', true);
            return;
        }

        const paymentToken = getSelectedPaymentToken();
        const paymentAmounts = computePaymentAmounts(treeDraft);
        const paymentAmount = paymentToken === 'usdc' ? paymentAmounts.usdc : paymentAmounts.sol;

        if (!paymentAmount || !Number.isFinite(paymentAmount) || paymentAmount <= 0) {
            setTreeSummaryStatus('Unable to calculate payment amount. Refresh pricing on the tree page.', true);
            return;
        }

        const paymentConfig = getPaymentConfig(treeDraft);
        
        mintButtons.forEach((btn) => {
            if (!btn.dataset.idleLabel) {
                btn.dataset.idleLabel = btn.innerHTML;
            }
            btn.disabled = true;
            btn.textContent = MINT_WORKING_LABEL;
        });
        
        try {
            // Step 1: Generate star via API
            setTreeSummaryStatus('Generating star...');
            const starResponse = await fetch('/api/generate-star', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    treeId: treeId,
                    params: this.params
                })
            });
            
            if (!starResponse.ok) {
                const starError = await starResponse.json().catch(() => ({}));
                throw new Error(starError.details || starError.error || 'Failed to generate star');
            }
            
            const starData = await starResponse.json();
            
            setTreeSummaryStatus(`Star generated! ID: ${starData.starId}`);
            
            // Step 2: Transfer payment to treasury
            setTreeSummaryStatus(`Sending ${paymentToken.toUpperCase()} payment...`);
            const paymentResult = await sendPaymentTransfer({
                amount: paymentAmount,
                walletAddress,
                config: paymentConfig,
                treeId,
                token: paymentToken
            });
            const txSignature = paymentResult?.signature || paymentResult;
            const treasuryAddress = paymentResult?.treasuryAddress || null;

            if (txSignature) {
                localStorage.setItem('lastCseasTx', txSignature);
            }
            if (treasuryAddress) {
                localStorage.setItem('lastTreasuryAddress', treasuryAddress);
            }

            // Step 3: Create bond with staking
            setTreeSummaryStatus('Creating reality bond...');
            const unlockPayload = buildUnlockPayload();
            
            const bondResponse = await fetch('/api/create-bond', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    starId: starData.starId,
                    treeId: treeId,
                    stakingMonths: parseInt(unlockPayload.selection),
                    walletAddress: walletAddress,
                    holderName: localStorage.getItem('holderName') || null,
                    dedication: localStorage.getItem('dedication') || null,
                    txSignature,
                    treasuryAddress,
                    paymentToken,
                    paymentAmount,
                    rpcUrl: paymentConfig.rpcUrl
                })
            });
            
            if (!bondResponse.ok) {
                const bondError = await bondResponse.json();
                throw new Error(bondError.message || 'Failed to create bond');
            }
            
            const bondData = await bondResponse.json();
            
            if (bondData.verificationStatus === 'verified') {
                const treasuryTag = bondData.verifiedTreasury
                    ? ` Treasury: ${bondData.verifiedTreasury.slice(0, 4)}...${bondData.verifiedTreasury.slice(-4)}.`
                    : '';
                setTreeSummaryStatus(`Bond created. CSEAS transfer verified.${treasuryTag}`);
            } else {
                setTreeSummaryStatus('Bond created. Waiting for on-chain verification...');
                try {
                    const verification = await waitForBondVerification({
                        bondRecordId: bondData.bondRecordId,
                        bondId: bondData.bondId,
                        txSignature,
                        rpcUrl: paymentConfig.rpcUrl
                    });
                    if (verification?.verified || verification?.verificationStatus === 'verified') {
                        bondData.verificationStatus = 'verified';
                        bondData.verifiedTreasury = verification.verifiedTreasury || bondData.verifiedTreasury;
                        setTreeSummaryStatus('Payment verified. Finalizing bond...');
                    } else {
                        setTreeSummaryStatus('Payment verification is pending. Please keep this tab open.', true);
                        mintButtons.forEach((btn) => {
                            btn.textContent = 'Verification Pending';
                            btn.disabled = true;
                        });
                        return;
                    }
                } catch (verificationError) {
                    setTreeSummaryStatus(verificationError.message || 'Verification pending. Please try again.', true);
                    mintButtons.forEach((btn) => {
                        btn.textContent = 'Verification Pending';
                        btn.disabled = true;
                    });
                    return;
                }
            }
            
            // Store bond data for next page
            localStorage.setItem('lastBondId', bondData.bondId);
            if (bondData.bondRecordId) {
                localStorage.setItem('lastBondRecordId', bondData.bondRecordId);
            }
            localStorage.setItem('lastDropCode', bondData.dropCode);
            localStorage.setItem('lastShortUrl', bondData.shortUrl);
            if (bondData.giftCode) {
                localStorage.setItem('lastGiftCode', bondData.giftCode);
                sessionStorage.setItem('lastGiftCode', bondData.giftCode);
            }
            if (bondData.giftUrl) {
                localStorage.setItem('lastGiftUrl', bondData.giftUrl);
                sessionStorage.setItem('lastGiftUrl', bondData.giftUrl);
            }
            if (bondData.recoveryCode) {
                localStorage.setItem('lastRecoveryCode', bondData.recoveryCode);
                sessionStorage.setItem('lastRecoveryCode', bondData.recoveryCode);
            }
            
            // Update button
            mintButtons.forEach((btn) => {
                btn.textContent = MINT_DONE_LABEL;
            });
            
            // Wait a moment then redirect to bond completion page
            setTimeout(() => {
                window.location.href = `/bond-completion?bondId=${bondData.bondId}&dropCode=${bondData.dropCode}`;
            }, 1500);
            
        } catch (error) {
            console.error('Bond creation error:', error);
            mintButtons.forEach((btn) => {
                btn.textContent = 'Failed';
            });
            setTreeSummaryStatus(`Error: ${error.message}`, true);
            
            setTimeout(() => {
                mintButtons.forEach((btn) => {
                    btn.innerHTML = btn.dataset.idleLabel || MINT_IDLE_LABEL;
                    btn.disabled = false;
                });
            }, 3000);
        }
    }

    rotateHaiku() {
        this.currentHaikuIndex = (this.currentHaikuIndex + 1) % haikus.length;
        const haiku = haikus[this.currentHaikuIndex];
        const haikuDiv = document.getElementById('haiku') || document.getElementById('haiku-drawer');
        if (haikuDiv) {
            haikuDiv.innerHTML = haiku.lines.map(line => 
                `<div class="haiku-line">${line}</div>`
            ).join('');
        }
    }
}

// Species detection and UI update functions
function updateSpeciesUI() {
    const draft = loadTreeDraft();
    const speciesBadge = document.getElementById('species-badge');
    const species = draft?.species ? speciesThemes[draft.species] : null;
    
    if (!species) {
        if (speciesBadge) speciesBadge.classList.add('hidden');
        getElements(['species-info', 'species-info-drawer']).forEach((el) => el.classList.add('hidden'));
        return;
    }
    
    // Update header badge
    if (speciesBadge) {
        const iconEl = document.getElementById('species-icon');
        const nameEl = document.getElementById('species-name');
        if (iconEl) iconEl.textContent = species.icon;
        if (nameEl) nameEl.textContent = species.name;
        speciesBadge.classList.remove('hidden');
    }
    
    // Update info panels (inline and drawer)
    [
        { container: 'species-info', icon: 'species-icon-lg', name: 'species-full-name', scientific: 'species-scientific', impact: 'species-impact' },
        { container: 'species-info-drawer', icon: 'species-icon-lg-drawer', name: 'species-full-name-drawer', scientific: 'species-scientific-drawer', impact: 'species-impact-drawer' }
    ].forEach((target) => {
        const container = document.getElementById(target.container);
        if (!container) return;
        const iconEl = document.getElementById(target.icon);
        const nameEl = document.getElementById(target.name);
        const scientificEl = document.getElementById(target.scientific);
        const impactEl = document.getElementById(target.impact);

        if (iconEl) iconEl.textContent = species.icon;
        if (nameEl) nameEl.textContent = species.name;
        if (scientificEl) scientificEl.textContent = species.scientific;
        if (impactEl) impactEl.textContent = `🌱 ${species.impact}`;
        container.classList.remove('hidden');
    });
}

// Countdown timer for unlock dates
function updateCountdownTimer() {
    const selection = getSelectedUnlockValue();
    const now = new Date();
    const unlockDate = computeUnlockDate(selection, now);
    const diff = unlockDate.getTime() - now.getTime();
    
    if (diff <= 0) {
        return 'Ready to harvest!';
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 365) {
        const years = Math.floor(days / 365);
        const remainingDays = days % 365;
        return `${years}y ${remainingDays}d remaining`;
    }
    if (days > 30) {
        const months = Math.floor(days / 30);
        const remainingDays = days % 30;
        return `${months}m ${remainingDays}d remaining`;
    }
    return `${days}d ${hours}h remaining`;
}

// Update staking option displays with countdowns
function updateStakingCountdowns() {
    const options = document.querySelectorAll('.staking-option');
    const selection = getSelectedUnlockValue();
    
    options.forEach((option) => {
        const input = option.querySelector('input');
        if (!input) return;
        const value = input.value;
        const unlockDate = computeUnlockDate(value, new Date());
        const now = new Date();
        const diff = unlockDate.getTime() - now.getTime();
        
        // Add countdown badge
        let countdownBadge = option.querySelector('.countdown-badge');
        const details = option.querySelector('.staking-details');
        if (!countdownBadge && details) {
            countdownBadge = document.createElement('div');
            countdownBadge.className = 'countdown-badge';
            details.appendChild(countdownBadge);
        }
        
        if (diff <= 0) {
            countdownBadge.textContent = '✓ Ready';
            countdownBadge.className = 'countdown-badge ready';
        } else {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            countdownBadge.textContent = `${days} days`;
            countdownBadge.className = 'countdown-badge';
        }
        
        // Highlight selected
        option.classList.toggle('selected', value === selection);
    });
}

// Impact calculation and display
function updateImpactDisplay() {
    const draft = loadTreeDraft();
    const selection = getSelectedUnlockValue();
    const option = unlockOptions[selection];
    
    if (!draft?.costUsd) return;
    
    const realTreesPlanted = Math.floor(draft.costUsd / 0.005); // Each ornament = 1 tree
    const multiplier = option?.multiplier || 1.0;
    const rewardedTrees = Math.floor(realTreesPlanted * multiplier);
    
    // Update impact in bond summary
    const impactEl = document.getElementById('bondSummaryImpact');
    if (!impactEl) {
        const summaryGrid = document.querySelector('.bond-summary .summary-grid');
        if (summaryGrid) {
            const impactDiv = document.createElement('div');
            impactDiv.className = 'summary-item';
            impactDiv.innerHTML = `
                <div class="summary-label">Real Trees</div>
                <div class="summary-value" id="bondSummaryImpact">0</div>
            `;
            summaryGrid.appendChild(impactDiv);
        }
    }
    
    const impactValueEl = document.getElementById('bondSummaryImpact');
    if (impactValueEl) {
        impactValueEl.textContent = `${realTreesPlanted} → ${rewardedTrees}`;
    }
}

// Secondary drawer toggle
function toggleSecondaryDrawer(force) {
    const drawer = document.getElementById('secondary-drawer');
    if (!drawer) return;

    const shouldOpen = typeof force === 'boolean' ? force : drawer.classList.contains('hidden');
    drawer.classList.toggle('hidden', !shouldOpen);
    document.body.classList.toggle('overflow-hidden', shouldOpen);

    if (shouldOpen) {
        renderTreePreview(loadTreeDraft());
        updateImpactDisplay();
    }
}

// Expose controls for inline handlers
window.generateNewStar = () => {
    if (!window.starGenerator) return;
    window.starGenerator.generateNewStar();
};

window.mintStar = () => {
    if (!window.starGenerator) {
        setTreeSummaryStatus('Star generator not ready yet.', true);
        return;
    }
    window.starGenerator.mintStar();
};

window.handleBondPayment = handleBondPayment;
window.toggleSecondaryDrawer = toggleSecondaryDrawer;

// Rarity badge update
function updateRarityBadge() {
    const score = window.starGenerator?.getRarityScore() || 0;
    const tier = window.starGenerator?.getRarityTier(score) || 'Common ★';
    
    const badge = document.getElementById('rarity-badge');
    if (badge) {
        const stars = badge.querySelector('.rarity-stars');
        const text = badge.querySelector('.rarity-text');
        
        if (stars) {
            const starCount = tier.includes('★★★★★') ? 5 : 
                             tier.includes('★★★★') ? 4 : 
                             tier.includes('★★★') ? 3 : 
                             tier.includes('★★') ? 2 : 1;
            stars.textContent = '★'.repeat(starCount);
        }
        if (text) text.textContent = tier.replace(/[★]/g, '').trim();
        
        // Add color class based on tier
        badge.className = 'rarity-badge';
        if (tier.includes('Legendary')) badge.classList.add('legendary');
        else if (tier.includes('Epic')) badge.classList.add('epic');
        else if (tier.includes('Rare')) badge.classList.add('rare');
        else if (tier.includes('Uncommon')) badge.classList.add('uncommon');
    }
}

// Initialize when the page loads
document.addEventListener('DOMContentLoaded', () => {
    const canvas = document.getElementById('glCanvas');
    if (canvas) {
        window.starGenerator = new StarGenerator(canvas);
        window.starGenerator.rotateHaiku(); // Display initial haiku
    }
    initUnlockSelection();
    updateTreeSummaryUI();
    updateSpeciesUI();
    updateStakingCountdowns();
    updateRarityBadge();
    updatePaymentUI();
    
    // Update countdowns every minute
    setInterval(updateStakingCountdowns, 60000);
    
    // Listen for staking option changes
    const radios = Array.from(document.querySelectorAll('input[name="bondUnlock"]'));
    radios.forEach((radio) => {
        radio.addEventListener('change', () => {
            updateStakingCountdowns();
            updateImpactDisplay();
        });
    });

    const paymentRadios = Array.from(document.querySelectorAll('input[name="bondPayment"]'));
    const storedToken = loadPaymentToken();
    paymentRadios.forEach((radio) => {
        if (radio.value === storedToken) {
            radio.checked = true;
        }
        radio.addEventListener('change', () => {
            if (radio.checked) {
                savePaymentToken(radio.value);
                updatePaymentUI();
            }
        });
    });
    
    // Listen for storage changes
    window.addEventListener('storage', (event) => {
        if (event.key === TREE_DRAFT_KEY) {
            updateTreeSummaryUI();
            updateSpeciesUI();
            updateImpactDisplay();
        }
    });
    
    // Handle window resize
    window.addEventListener('resize', () => {
        renderTreePreview(loadTreeDraft());
    });
});
